import Typography from '@mui/material/Typography';
import { useGoals } from 'features/goals/hooks/use-goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchGoalCard } from 'shared/components/connected/search-goal-card';
import { SearchLifeAreaCard } from 'shared/components/connected/search-life-area-card';
import { SearchTagCard } from 'shared/components/connected/search-tag-card';
import { SearchTaskCard } from 'shared/components/connected/search-task-card';
import { useFocusChildrenWithArrows } from 'shared/hooks/use-focus-children-with-arrows';
import { useTextFilteredGoals } from 'shared/hooks/use-text-filtered-goals';
import { useTextFilteredLifeAreas } from 'shared/hooks/use-text-filtered-life-areas';
import { useTextFilteredTags } from 'shared/hooks/use-text-filtered-tags';
import { useTextFilteredTasks } from 'shared/hooks/use-text-filtered-tasks';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { ActionEvents } from 'shared/services/tracking';

import * as Styled from './search-results.style';

export type SearchResultsProps = {
  searchValue: string;
  onClose: () => void;
};

export const SearchResults: React.FC<SearchResultsProps> = ({
  searchValue,
  onClose,
}) => {
  const track = useTrackEvents();
  const listRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const tasks = useTextFilteredTasks(searchValue);
  const goals = useTextFilteredGoals(searchValue);
  const lifeAreas = useTextFilteredLifeAreas(searchValue);
  const tags = useTextFilteredTags(searchValue);

  const allGoals = useGoals();
  const allLifeAreas = useLocalizedLifeAreas();

  const _onClose = () => {
    track(ActionEvents.SearchResultClicked, undefined);
    onClose();
  };

  useFocusChildrenWithArrows(listRef, {
    querySelectors: ['[tabindex]:not([tabindex="-1"])'],
  });

  return tasks.length || goals.length || lifeAreas.length || tags.length ? (
    <Styled.ResultsContainer ref={listRef}>
      {!!lifeAreas.length && (
        <>
          <Styled.Title>
            {t('dialogs.search.list.title.life-areas')}
          </Styled.Title>
          <Styled.List>
            {lifeAreas.map((lifeArea) => (
              <Styled.ListItem key={lifeArea.id}>
                <SearchLifeAreaCard
                  lifeArea={lifeArea}
                  onClick={_onClose}
                  tabbable
                />
              </Styled.ListItem>
            ))}
          </Styled.List>
        </>
      )}

      {!!goals.length && (
        <>
          <Styled.Title>{t('dialogs.search.list.title.goals')}</Styled.Title>
          <Styled.List>
            {goals.map((goal) => (
              <Styled.ListItem key={goal.id}>
                <SearchGoalCard
                  goal={goal}
                  onClick={_onClose}
                  tabbable
                  goals={allGoals}
                  lifeAreas={allLifeAreas}
                />
              </Styled.ListItem>
            ))}
          </Styled.List>
        </>
      )}

      {!!tasks.length && (
        <>
          <Styled.Title>{t('dialogs.search.list.title.tasks')}</Styled.Title>
          <Styled.List>
            {tasks.map((task) => (
              <Styled.ListItem key={task.id}>
                <SearchTaskCard task={task} onClick={_onClose} tabbable />
              </Styled.ListItem>
            ))}
          </Styled.List>
        </>
      )}

      {!!tags.length && (
        <>
          <Styled.Title>{t('dialogs.search.list.title.tags')}</Styled.Title>
          <Styled.List>
            {tags.map((tag) => (
              <Styled.ListItem key={tag.id}>
                <SearchTagCard tag={tag} onClick={_onClose} tabbable />
              </Styled.ListItem>
            ))}
          </Styled.List>
        </>
      )}
    </Styled.ResultsContainer>
  ) : (
    <Styled.EmptyContainer>
      <Typography variant="subtitle1">
        {t('general.labels.search.no-results')}
      </Typography>
    </Styled.EmptyContainer>
  );
};
